import { useEffect, useRef, useState } from "react";
import {
  FolderUp, Loader2, CheckCircle2, AlertTriangle, FileText, X, RefreshCw,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";

const MAX_FILES = 100;

/**
 * Bulk invoice uploader.
 *
 * Accepts up to MAX_FILES PDFs/images, ships them to
 * ``POST /finance/invoices/bulk-upload`` and polls the returned job while the
 * worker runs in the background (VirusTotal → OCR → dedupe → insert). The
 * owner can close the dialog and the job keeps running; reopening the dialog
 * reattaches to the still-running job.
 */
export default function BulkInvoiceUploadDialog({ restaurantId, onImported }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  // Rehydrate the most recent running job when the dialog opens.
  useEffect(() => {
    if (!open || !restaurantId) return;
    api.request(`/restaurants/${restaurantId}/finance/invoices/bulk-jobs?limit=1`)
      .then(r => {
        const last = (r?.items || [])[0];
        if (last && ["queued", "processing"].includes(last.status)) {
          setJob(last);
          startPolling(last.job_id);
        }
      })
      .catch(() => {});
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, restaurantId]);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.request(`/restaurants/${restaurantId}/finance/invoices/bulk-jobs/${jobId}`);
        setJob(r);
        if (["completed", "failed"].includes(r.status)) {
          stopPolling();
          onImported?.();
          if (r.imported > 0) {
            toast.success(`${r.imported} factura${r.imported === 1 ? "" : "s"} importada${r.imported === 1 ? "" : "s"}`);
          }
          if (r.skipped > 0) {
            toast.info(`${r.skipped} omitida${r.skipped === 1 ? "" : "s"} (duplicados)`);
          }
        }
      } catch { /* keep polling */ }
    }, 1500);
  };

  const onPick = (list) => {
    const incoming = Array.from(list || []);
    const merged = [...files, ...incoming].slice(0, MAX_FILES);
    setFiles(merged);
  };

  const removeAt = (idx) => {
    setFiles(f => f.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach(f => form.append("files", f));
      const url = `${api.baseUrl}/restaurants/${restaurantId}/finance/invoices/bulk-upload`;
      const resp = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: api.getAuthOnlyHeaders(),
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Error subiendo el lote");
      }
      const data = await resp.json();
      setFiles([]);
      setJob({ ...data, processed: 0, imported: 0, skipped: 0, failed: 0, errors: [] });
      startPolling(data.job_id);
      toast.success(`Lote enviado — ${data.total_files} archivos en cola`);
    } catch (e) {
      toast.error(e.message || "Error subiendo el lote");
    } finally {
      setUploading(false);
    }
  };

  const retryFailed = async () => {
    if (!job?.job_id) return;
    setRetrying(true);
    try {
      const r = await api.request(
        `/restaurants/${restaurantId}/finance/invoices/bulk-jobs/${job.job_id}/retry-failed`,
        { method: "POST" },
      );
      setJob({ ...r, processed: 0, imported: 0, skipped: 0, failed: 0, errors: [] });
      startPolling(r.job_id);
      toast.success(`Reintentando ${r.total_files} archivo${r.total_files === 1 ? "" : "s"}…`);
    } catch (e) {
      toast.error(e.message || "No se pudo reintentar");
    } finally {
      setRetrying(false);
    }
  };

  const isRunning = job && ["queued", "processing"].includes(job.status);
  const isDone = job && ["completed", "failed"].includes(job.status);
  const pct = job && job.total_files > 0
    ? Math.round(((job.processed || 0) / job.total_files) * 100)
    : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) stopPolling(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="bulk-invoices-btn">
          <FolderUp className="h-3.5 w-3.5 mr-1.5" />
          Subir lote de facturas
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="bulk-invoices-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderUp className="h-5 w-5 text-primary" />
            Subida masiva de facturas
          </DialogTitle>
          <DialogDescription>
            Selecciona hasta {MAX_FILES} PDFs o imágenes. La IA extraerá los
            datos de cada uno y bloqueará automáticamente las duplicadas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* File picker */}
          {!isRunning && (
            <>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => { onPick(e.target.files); e.target.value = ""; }}
                data-testid="bulk-invoices-file-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-primary/30 bg-primary/5 rounded-xl py-8 px-4 text-center hover:bg-primary/10 transition"
                data-testid="bulk-invoices-picker"
              >
                <FolderUp className="h-8 w-8 mx-auto text-primary/70 mb-2" />
                <p className="text-sm font-medium">
                  {files.length > 0
                    ? `${files.length} archivo${files.length === 1 ? "" : "s"} seleccionado${files.length === 1 ? "" : "s"}`
                    : "Seleccionar facturas"}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  PDF, JPG, PNG — máx {MAX_FILES}
                </p>
              </button>

              {files.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y text-xs">
                  {files.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/40"
                      data-testid={`bulk-invoices-queued-${i}`}
                    >
                      <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {(f.size / 1024).toFixed(0)} KB
                      </span>
                      <button
                        type="button"
                        onClick={() => removeAt(i)}
                        className="text-muted-foreground hover:text-foreground shrink-0"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Progress */}
          {isRunning && (
            <div
              className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 space-y-2"
              data-testid="bulk-invoices-progress"
            >
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-900">
                  Procesando {job.processed || 0} / {job.total_files}
                </span>
                <span className="ml-auto text-xs font-bold text-indigo-700">{pct}%</span>
              </div>
              <div className="h-2 w-full bg-indigo-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600 rounded-full transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="text-[11px] text-indigo-800/80">
                {job.imported || 0} importadas · {job.skipped || 0} duplicadas · {job.failed || 0} errores
              </div>
              <p className="text-[10px] text-indigo-700/70">
                Puedes cerrar esta ventana — el proceso seguirá en segundo plano.
              </p>
            </div>
          )}

          {/* Completed summary */}
          {isDone && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-2"
                 data-testid="bulk-invoices-done">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                <span className="text-sm font-semibold text-emerald-900">
                  Lote procesado
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded bg-card p-2">
                  <div className="text-lg font-bold text-emerald-700">{job.imported || 0}</div>
                  <div className="text-[10px] text-muted-foreground uppercase">Importadas</div>
                </div>
                <div className="rounded bg-card p-2">
                  <div className="text-lg font-bold text-amber-700">{job.skipped || 0}</div>
                  <div className="text-[10px] text-muted-foreground uppercase">Duplicadas</div>
                </div>
                <div className="rounded bg-card p-2">
                  <div className="text-lg font-bold text-red-700">{job.failed || 0}</div>
                  <div className="text-[10px] text-muted-foreground uppercase">Errores</div>
                </div>
              </div>
              {job.errors?.length > 0 && (
                <details className="text-[11px] text-foreground">
                  <summary className="cursor-pointer flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 text-amber-700" />
                    Ver detalle ({job.errors.length})
                  </summary>
                  <ul className="mt-1.5 space-y-1 max-h-32 overflow-y-auto">
                    {job.errors.map((e, i) => (
                      <li key={i} className="px-2 py-1 rounded bg-card">
                        <b>{e.filename}</b> — {e.detail}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {(job.retryable_count || 0) > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={retryFailed}
                  disabled={retrying}
                  className="w-full mt-1 border-amber-300 bg-card hover:bg-amber-50"
                  data-testid="bulk-invoices-retry-failed-btn"
                >
                  {retrying
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <RefreshCw className="h-3.5 w-3.5 mr-1.5 text-amber-700" />}
                  Reintentar {job.retryable_count} archivo{job.retryable_count === 1 ? "" : "s"} con error
                </Button>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {isRunning ? (
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cerrar (seguirá en segundo plano)
            </Button>
          ) : isDone ? (
            <>
              <Button variant="outline" onClick={() => { setJob(null); setFiles([]); }}>
                Subir otro lote
              </Button>
              <Button onClick={() => setOpen(false)}>Listo</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button
                onClick={submit}
                disabled={!files.length || uploading}
                data-testid="bulk-invoices-submit"
              >
                {uploading
                  ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <FolderUp className="h-3.5 w-3.5 mr-1.5" />}
                Subir {files.length || ""} {files.length === 1 ? "factura" : "facturas"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
